import { closestCenter, DndContext, DragEndEvent, DragOverlay, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import ClearIcon from '@mui/icons-material/Clear';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DoneIcon from '@mui/icons-material/Done';
import { Box, Button, CircularProgress, Divider, IconButton, List, ListItem, ListItemButton, Stack, Tooltip, Typography } from '@mui/material';
import { Dashboard } from '@protos/v2/dashboard';
import { useDashboardContext } from '@shared/contexts/DashboardContext';
import theme from '@shared/themes/darkTheme';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardItem } from './DashboardItem';
import { useDashboardList } from './useDashboardList';
import { useSharedDashboards } from './useSharedDashboards';
import { useUserContext } from '@shared/contexts/UserContextProvider';
import { MarketsDashboardItem, NewsDashboardItem } from '@features/dashboard/freemium/components/FreemiumDashboardItems';
import { checkIsPremiumUser } from '@utils/permissions';
import { config } from '@services/context';

type Props = {
  dashboardToDelete: Dashboard | null;
  setDashboardToDelete: (dashboard: Dashboard | null) => void;
  setDashboardToShare: (dashboard: Dashboard | null) => void;
  setAnchorEl: (anchor: HTMLElement | null) => void;
};

export const DashboardList = ({ dashboardToDelete, setDashboardToDelete, setAnchorEl, setDashboardToShare }: Props) => {
  const navigate = useNavigate();
  const {
    updateDashboard,
    deleteDashboard,
    addDashboard,
    currentDashboard: selectedDashboard,
    allDashboards,
    setCurrentDashboardById,
    isDashboardLoading,
    sortedDashboardList,
    updateDashboardOrder,
    dashboardOrderLoading: settingsLoading,
  } = useDashboardContext();
  const { onCreateNewDashboard } = useDashboardList(
    updateDashboard,
    deleteDashboard,
    addDashboard,
    setCurrentDashboardById,
    navigate,
    allDashboards,
    selectedDashboard
  );
  const { sharedDashboards, manageSharedDashboard } = useSharedDashboards();
  const [hoveredDashboardId, setHoveredDashboardId] = useState<number | null>(null);
  const [draggedDashboardId, setDraggedDashboardId] = useState<number | null>(null);
  const user = useUserContext();
  const isPremiumUser = checkIsPremiumUser(user);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // Don't start drag unless pointer moves by 5px
      },
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = sortedDashboardList.findIndex(d => d.id === active.id);
      const newIndex = sortedDashboardList.findIndex(d => d.id === over.id);
      const newOrder = arrayMove(sortedDashboardList, oldIndex, newIndex);
      updateDashboardOrder(newOrder);
    }
  };

  return (
    <>
      <Stack>
        <Typography variant="caption" sx={{ fontSize: 10, marginLeft: 1 }}>
          FREEMIUM DASHBOARDS
        </Typography>
        <List>
          <NewsDashboardItem />
          {config.isDev && <MarketsDashboardItem />}
        </List>
      </Stack>
      <Divider />
      {isPremiumUser && (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" marginTop={1} height={10} marginBottom={1}>
            <Typography variant="caption" sx={{ fontSize: 10, marginLeft: 1 }}>
              YOUR DASHBOARDS
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onCreateNewDashboard()}
            sx={{ fontSize: 10, height: 25, padding: '0px  !important', marginLeft: 1, marginRight: 1 }}
          >
            + New Dashboard
          </Button>
          <Stack minHeight={70}>
            {!isDashboardLoading && !settingsLoading ? (
              sortedDashboardList.length > 0 ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={event => {
                    setDraggedDashboardId(Number(event.active.id));
                  }}
                  onDragEnd={event => {
                    setDraggedDashboardId(null);
                    handleDragEnd(event);
                  }}
                >
                  <SortableContext items={sortedDashboardList.map(d => d.id)} strategy={verticalListSortingStrategy}>
                    <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                      {sortedDashboardList.map(dashboard => (
                        <DashboardItem
                          key={dashboard.id}
                          dashboard={dashboard}
                          dashboardToDelete={dashboardToDelete}
                          setAnchorEl={setAnchorEl}
                          setDashboardToShare={setDashboardToShare}
                          setDashboardToDelete={setDashboardToDelete}
                        />
                      ))}
                    </List>
                    <DragOverlay>
                      {draggedDashboardId ? (
                        <DashboardItem
                          dashboard={sortedDashboardList.find(d => d.id === draggedDashboardId)!}
                          hideIcons={true}
                          dashboardToDelete={dashboardToDelete}
                          setAnchorEl={setAnchorEl}
                          setDashboardToShare={setDashboardToShare}
                          setDashboardToDelete={setDashboardToDelete}
                        />
                      ) : null}
                    </DragOverlay>
                  </SortableContext>
                </DndContext>
              ) : (
                <Typography variant="caption" sx={{ fontSize: 11, marginLeft: 2, marginTop: 1, marginBottom: 1 }}>
                  No Dashboards Found
                </Typography>
              )
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <CircularProgress size={15} sx={{ alignSelf: 'center' }} />
              </Box>
            )}
          </Stack>
        </>
      )}

      {isPremiumUser && sharedDashboards.length > 0 && (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" marginTop={1} height={10} marginBottom={1}>
            <Typography variant="caption" sx={{ fontSize: 10, marginLeft: 1 }}>
              DASHBOARDS SHARED WITH YOU
            </Typography>
          </Box>
          <Stack>
            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {sharedDashboards
                .sort((a, b) => a.id - b.id)
                .map(dashboard => (
                  <ListItem key={dashboard.id} disablePadding>
                    <ListItemButton
                      onMouseEnter={() => setHoveredDashboardId(dashboard.dashboard_id)}
                      onMouseLeave={() => setHoveredDashboardId(null)}
                      sx={{
                        padding: 1,
                        '&:hover': { backgroundColor: '#142535 !important' },
                        display: 'flex',
                        flexDirection: 'row',
                        flex: 1,
                        alignItems: 'center',
                        gap: 1,
                        cursor: 'default',
                      }}
                    >
                      <DashboardIcon sx={{ fontSize: 15, marginRight: 1 }} />
                      <Typography variant="body1" sx={{ fontSize: 11, mb: 0.2 }}>
                        {dashboard.dashboard_name || `Dashboard #${dashboard.dashboard_id}`}
                      </Typography>

                      {(hoveredDashboardId === dashboard.dashboard_id || (dashboardToDelete && dashboardToDelete.id === dashboard.dashboard_id)) && (
                        <Box display="flex" alignItems="center" gap={1} marginLeft="auto">
                          <Tooltip title="Reject Dashboard">
                            <IconButton
                              sx={{ fontSize: 15, padding: 0 }}
                              onClick={() => {
                                manageSharedDashboard(dashboard.id, false);
                              }}
                            >
                              <ClearIcon sx={{ fontSize: 15, padding: 0, color: theme.palette.background.sell, '&:hover': { color: 'white' } }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Accept Dashboard">
                            <IconButton
                              sx={{ fontSize: 15, padding: 0 }}
                              onClick={() => {
                                manageSharedDashboard(dashboard.id, true);
                              }}
                            >
                              <DoneIcon
                                sx={{
                                  fontSize: 15,
                                  padding: 0,
                                  color: theme.palette.background.buy,
                                  '&:hover': { color: 'white' },
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </ListItemButton>
                  </ListItem>
                ))}
            </List>
          </Stack>
        </>
      )}
    </>
  );
};
